import React, { MouseEventHandler, ReactNode, useCallback, useMemo, useState } from 'react'
import { preventAll } from '../../../../_utils/utils'
import { getRemoveNodeMutations } from '../../helpers'
import { CopyTypes, JourneyAction } from '../../types/journey-context'
import { applyEdgeChanges, applyNodeChanges, useReactFlow } from '@xyflow/react'
import { Button } from 'antd'
import { CheckCircleOutlined, CloseOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons'
import { useJourneyContext } from '../../context'
import { ActionStep, DelayStep, JourneyNode } from '../../types/journey-nodes'
import { JourneyBuilderMode } from '../../enums'
import { StepType } from '@pushly/models/lib/enums/journeys'

export const JourneyNodeInteractionActions = ({ nodeId }: { nodeId: string }) => {
    const [state, dispatch] = useJourneyContext()
    const { getNode, getNodes, getEdges, setNodes, setEdges } = useReactFlow<JourneyNode>()

    const node = getNode(nodeId)
    if (!node) {
        return null
    }

    const [isCopied, setIsCopied] = useState(false)

    const handleRemoveNode: MouseEventHandler<HTMLSpanElement> = useCallback(
        (ev) => {
            preventAll(ev)

            dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
            const [nodeChanges, edgeChanges] = getRemoveNodeMutations(node, getNodes(), getEdges())
            dispatch({ action: JourneyAction.ON_NODE_REMOVE, data: node })

            setEdges((eds) => applyEdgeChanges(edgeChanges, eds))
            setNodes((nodes) => applyNodeChanges(nodeChanges, nodes))
        },
        [nodeId, node],
    )

    const handleCopyNode = useCallback(
        (ev) => {
            preventAll(ev)

            if (![StepType.TRIGGER, StepType.EXIT, StepType.GLOBAL_TRIGGER].includes(node.data.step.type)) {
                dispatch({
                    action: JourneyAction.SET_COPY_STATE,
                    data: { type: CopyTypes.COPY, node: node as JourneyNode<ActionStep | DelayStep> },
                })

                setIsCopied(true)
                setTimeout(() => setIsCopied(false), 2000)
            }
        },
        [nodeId, node],
    )

    const handleCutNode = useCallback(
        (ev) => {
            preventAll(ev)
            dispatch({
                action: JourneyAction.SET_COPY_STATE,
                data: { type: CopyTypes.CUT, node: node as JourneyNode<ActionStep | DelayStep> },
            })
            handleRemoveNode(ev)
        },
        [nodeId, node],
    )

    const handleNodeClick: MouseEventHandler<HTMLDivElement> = useCallback(
        (ev) => {
            if (![StepType.TRIGGER, StepType.EXIT, StepType.GLOBAL_TRIGGER].includes(node.data.step.type)) {
                state.callbacks.onNodeClick(ev, node)
            }
        },
        [state.mode, node],
    )

    const nodeInteractionButtons = useMemo(() => {
        const shownButtons: ReactNode[] = []

        if (state.mode === JourneyBuilderMode.READONLY) {
            shownButtons.push(
                <Button
                    key={`jnv-btn-${nodeId}`}
                    className="journey-node-view-button"
                    icon={<EyeOutlined onClick={handleNodeClick} />}
                    aria-label="View Node"
                />,
            )

            return shownButtons
        }

        if (node.data.uiMeta.invalid?.options?.includes('edit') || node.data.uiMeta.invalid === undefined) {
            shownButtons.push(
                <Button
                    key={`jne-btn-${nodeId}`}
                    className="journey-node-edit-button"
                    icon={<EditOutlined onClick={handleNodeClick} />}
                />,
            )
        }

        // @todo - come back to this and figure proper handling
        // of copied and updating notifications
        // if (!node.data.uiMeta.invalid) {
        //     shownButtons.push(
        //         <Button
        //             key={`jnc-btn-${nodeId}`}
        //             className="journey-node-copy-button"
        //             icon={<CopyOutlined onClick={handleCopyNode} />}
        //         />,
        //         <Button
        //             key={`jnct-btn-${nodeId}`}
        //             className="journey-node-cut-button"
        //             icon={<ScissorOutlined onClick={handleCutNode} />}
        //         />,
        //     )
        // }

        shownButtons.push(
            <Button
                key={`jnr-btn-${nodeId}`}
                className="journey-node-remove-button"
                icon={<CloseOutlined onClick={handleRemoveNode} />}
                aria-label="Edit Node"
            />,
        )
        return shownButtons
    }, [nodeId, node, node.data.uiMeta.invalid])

    return (
        <div className={`custom-node-interaction-actions fade-in-out ${isCopied ? 'transition-copied' : ''}`}>
            {isCopied && (
                <span className="transition-copied">
                    <CheckCircleOutlined /> <span>Copied!</span>
                </span>
            )}
            {nodeInteractionButtons}
        </div>
    )
}
